import React, { useMemo } from "react";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FormProvider, useForm } from "react-hook-form";

import { useOrganizationUser } from "src/api/taktile/queries";
import { Card } from "src/base-components/Card";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { FlowFolderListItem } from "src/clients/flow-api";
import { OrganizationUser, RoleInDB } from "src/clients/taktile-api";
import { Avatar } from "src/design-system/Avatar";
import { EmptyState } from "src/design-system/EmptyState";
import { Skeleton } from "src/design-system/Skeleton";
import { Tabs } from "src/design-system/Tabs";
import { useFolders } from "src/flowsOverview/v2/folderQueries";
import { DashboardContent } from "src/layout/DashboardContent";
import { PageHeader } from "src/layout/PageHeader";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { DashboardPageParams, getUrlToSettingsPage } from "src/router/urls";
import { SettingsSubpages } from "src/settings/SettingsSubpages";
import { useUserWorkspaceRoles } from "src/userManagement/userPermissions/queries";
import { UserGranularRolesTable } from "src/userManagement/userPermissions/UserGranularRolesTable";
import { UserGranularRolesTableSkeleton } from "src/userManagement/userPermissions/UserGranularRolesTableSkeleton";
import { UserPermissionsActions } from "src/userManagement/userPermissions/UserPermissionsActions";
import {
  UserSummaryHeader,
  UserSummaryHeaderSkeleton,
} from "src/userManagement/userPermissions/UserSummaryHeader";
import {
  getInitialFormValues,
  UserPermissionsFormValues,
} from "src/userManagement/userPermissions/utils";
import { errorMessage } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { getUserName } from "src/utils/user";

export const UserPermissionsDetailPage: React.FC = () => {
  const { orgId, workspace } = useWorkspaceContext();
  const { userId } = useParamsDecode<
    DashboardPageParams & { userId: string }
  >();
  const { isLoading: isUserLoading, data: user } = useOrganizationUser(
    orgId,
    userId,
  );
  const { data: roles, isLoading: isRolesLoading } = useUserWorkspaceRoles({
    userId,
  });
  const folders = useFolders({ workspaceId: workspace.id });

  const isLoading = isUserLoading || isRolesLoading || folders.isLoading;

  if (!isLoading && !folders.isError && user && roles) {
    return (
      <UserPermissions
        key={userId}
        folders={folders.data?.folders ?? []}
        roles={roles}
        user={user}
        userId={userId}
      />
    );
  }

  return (
    <UserPermissionsShell isUserLoading={isUserLoading} user={user}>
      <UserSummaryHeaderSkeleton />
      <WorkspacePermissionsCard>
        {folders.isError ? (
          <EmptyState
            dataLoc="user-granular-roles-error-state"
            description={errorMessage(folders.error)}
            headline="Error loading folders"
            icon={faTriangleExclamation}
            variant="error"
          />
        ) : (
          <UserGranularRolesTableSkeleton />
        )}
      </WorkspacePermissionsCard>
    </UserPermissionsShell>
  );
};

const UserPermissions: React.FC<{
  user: OrganizationUser;
  userId: string;
  roles: RoleInDB[];
  folders: FlowFolderListItem[];
}> = ({ user, userId, roles, folders }) => {
  const initialFormValues = useMemo(
    () => getInitialFormValues({ user, roles, folders }),
    [user, roles, folders],
  );
  const formMethods = useForm<UserPermissionsFormValues>({
    defaultValues: initialFormValues,
  });

  return (
    <FormProvider {...formMethods}>
      <UserPermissionsShell
        actions={
          <UserPermissionsActions
            initialFormValues={initialFormValues}
            userId={userId}
          />
        }
        isUserLoading={false}
        user={user}
      >
        <UserSummaryHeader user={user} />
        <WorkspacePermissionsCard>
          <UserGranularRolesTable folders={folders} />
        </WorkspacePermissionsCard>
      </UserPermissionsShell>
    </FormProvider>
  );
};

const UserPermissionsShell: React.FC<{
  user?: OrganizationUser;
  isUserLoading: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ user, isUserLoading, actions, children }) => {
  const { orgId, workspace } = useWorkspaceContext();
  const usersPageUrl = getUrlToSettingsPage(
    orgId,
    workspace.id,
    SettingsSubpages.Users,
    { keepFlags: true },
  );

  return (
    <DashboardContent
      Header={
        <PageHeader
          breadcrumbs={[
            {
              key: "users",
              breadcrumb: (
                <PageHeader.Breadcrumb
                  dataLoc="users-breadcrumb"
                  href={usersPageUrl}
                  label="Users"
                  hideIcon
                />
              ),
            },
            {
              key: "user-permissions-detail-page",
              breadcrumb: (
                <PageHeader.Breadcrumb
                  dataLoc="user-permissions-detail-page"
                  href={usersPageUrl}
                  label={<UserDetails isLoading={isUserLoading} user={user} />}
                  active
                />
              ),
            },
          ]}
          sideContent={actions}
        />
      }
    >
      <div className="flex flex-col gap-4">{children}</div>
    </DashboardContent>
  );
};

const WorkspacePermissionsCard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Card variant="inverted" fullWidth>
    <Card.Content>
      <Tabs
        tabs={[
          {
            key: "workspace-permissions",
            label: "Workspace Permissions",
            content: children,
          },
        ]}
      />
    </Card.Content>
  </Card>
);

const UserDetails: React.FC<{
  user?: OrganizationUser;
  isLoading: boolean;
}> = ({ user, isLoading }) => {
  if (isLoading || !user) {
    return (
      <div className="w-46">
        <Skeleton height="h-5" />{" "}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Avatar isLoading={false} user={user} size="sm" />
      <span className="font-inter-semibold-13px text-gray-800">
        {getUserName(user)}
      </span>
      <TruncatedIdPill id={user.id} variant="last-five" />
    </div>
  );
};
